import api from "@/api";
import { JSConn } from "@/utilities/js-conn";
import { TemplateWorkOrderLot } from "@/store/modules/templateWorkOrderLots";
import { TemplateWorkOrderTask } from "@/store/modules/templateWorkOrderTasks";
import { map as Rmap } from "ramda";

const itemDetails = {
  pluralName: "template_work_orders",
  singularName: "template_work_order",
};

const emptyTemplateWorkOrder = {
  id: null,
  template_name: null,
  template_description: null,
  description: null,
  commodity_id: null,
};

const apiParamsBuilder = (jsConn: JSConn) => {
  return {
    id: jsConn.formParams.id,
    template_work_order: {
      ...jsConn.formParams,
    },
  };
};

interface TemplateWorkOrder {
  id: string;
  template_name: string;
  template_description: string;
  description: string;
  cut_number: number;
  duration: number;
  duration_unit: string;
  commodity: string;
  commodity_id: string;
  template_work_order_lots: TemplateWorkOrderLot[];
  template_work_order_tasks: TemplateWorkOrderTask[];
}

interface TemplateWorkOrderState {
  templateWorkOrders: TemplateWorkOrder[];
  templateWorkOrder: TemplateWorkOrder | {};
}

const state: TemplateWorkOrderState = {
  templateWorkOrders: [],
  templateWorkOrder: {},
};

const mutations = {
  LOAD_TEMPLATE_WORK_ORDERS(
    state: TemplateWorkOrderState,
    payload: TemplateWorkOrder[],
  ) {
    state.templateWorkOrders = payload;
  },
  LOAD_TEMPLATE_WORK_ORDER(
    state: TemplateWorkOrderState,
    payload: TemplateWorkOrder,
  ) {
    state.templateWorkOrder = payload;
  },
};

const actions = {
  needTemplateWorkOrders(context: any, jsConn: JSConn) {
    jsConn.apiParams = jsConn.formParams;
    return api.getItems(jsConn, itemDetails).then(jsConn => {
      const apiResponse = jsConn.response;
      context.commit("LOAD_TEMPLATE_WORK_ORDERS", apiResponse);
      return Promise.resolve(jsConn);
    });
  },
  needTemplateWorkOrder(context: any, jsConn: JSConn) {
    jsConn.apiParams = apiParamsBuilder(jsConn);
    return api.getItem(jsConn, itemDetails).then(jsConn => {
      const apiResponse = jsConn.response;
      context.commit("LOAD_TEMPLATE_WORK_ORDER", apiResponse);
      context.commit(
        "LOAD_TEMPLATE_WORK_ORDER_TASKS",
        apiResponse.template_work_order_tasks,
        { root: true },
      );
      context.commit(
        "LOAD_TEMPLATE_WORK_ORDER_LOTS",
        apiResponse.template_work_order_lots,
        { root: true },
      );
      return Promise.resolve(jsConn);
    });
  },
  createTemplateWorkOrder(context: any, jsConn: JSConn) {
    jsConn.apiParams = apiParamsBuilder(jsConn);
    return api
      .createItem(jsConn, itemDetails)
      .then(jsConn => {
        context.commit("LOAD_TEMPLATE_WORK_ORDER", jsConn.response);
        return Promise.resolve(jsConn);
      })
      .catch(jsConn => {
        return Promise.reject(jsConn);
      });
  },
  updateTemplateWorkOrder(context: any, jsConn: JSConn) {
    jsConn.apiParams = apiParamsBuilder(jsConn);
    return api
      .updateItem(jsConn, itemDetails)
      .then(jsConn => {
        context.commit("LOAD_TEMPLATE_WORK_ORDER", jsConn.response);
        return Promise.resolve(jsConn);
      })
      .catch(jsConn => {
        return Promise.reject(jsConn);
      });
  },
  deleteTemplateWorkOrder(context: any, jsConn: JSConn) {
    jsConn.apiParams = apiParamsBuilder(jsConn);

    return api
      .deleteItem(jsConn, itemDetails)
      .then(jsConn => {
        context.commit("LOAD_TEMPLATE_WORK_ORDER", emptyTemplateWorkOrder);
        return Promise.resolve(jsConn);
      })
      .catch(jsConn => {
        return Promise.reject(jsConn);
      });
  },
};

const getters = {
  templateWorkOrders: (state: TemplateWorkOrderState) => {
    return state.templateWorkOrders;
  },
  templateWorkOrder: (state: TemplateWorkOrderState) => {
    return state.templateWorkOrder;
  },
  templateWorkOrdersForAutocomplete: (state: TemplateWorkOrderState) => {
    return Rmap(template => {
      return {
        value: template.id,
        label: template.template_name,
      };
    }, state.templateWorkOrders);
  },
  templateWorkOrdersForTable: (state: TemplateWorkOrderState) => {
    return state.templateWorkOrders;
  },
};

const templateWorkOrdersModule = {
  state,
  mutations,
  actions,
  getters,
};

export default templateWorkOrdersModule;
